'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import { ReactNode, useState } from 'react';

import { useCurrency } from '@/hooks/useCurrency';

import { ProjectionScenario } from '../data/rateProjections';
import { BillPoint, ScenarioRange } from '../engine';

import { roundFive, signedPercent } from './chartFormat';

type Props = {
  /** 2005–2020 back-cast revealed by the left "show the past" toggle */
  deepHistory?: BillPoint[];
  points: BillPoint[];
  /** Spread across all scenarios, rendered as the fan's shaded band */
  scenarioRange?: ScenarioRange;
  /** The Forecast Type control, rendered as chart furniture below the plot */
  scenarioSelector?: ReactNode;
};

// The monthly bill is the "do nothing" story — maroon reads as the warning
// color. TODO(bill-projection): confirm at the design gate.
const BAR_COLOR = 'uiChartGas';

// Taller plot on desktop so the page's thesis chart outranks the section charts
const PLOT_H = { desktop: 240, mobile: 150 };
const MIN_BAR_H = 6;
// Reserves room inside the fan SVG for $ labels above the band
const FAN_TOP_PAD = { desktop: 22, mobile: 16 };
const CAPTION_H = { desktop: 18, mobile: 12 };
// Dense mobile: toggle + 22 five-pixel columns + fan must fit the 272px card a 320px viewport leaves
const FAN_W = { desktop: 400, desktopDense: 340, mobile: 150, mobileDense: 100 };

const BAR_W = {
  dense: { _: '5px', md: '12px' },
  normal: { _: '16px', md: '30px' },
};
const ROW_GAP = {
  dense: { _: '1px', md: 1 },
  normal: { _: 1, md: 2 },
};

type BarKind = 'anchor' | 'history';

type BarSlot = {
  kind: BarKind;
  point: BillPoint;
};

const forecastSourceCopy = (point: BillPoint): string => {
  if (point.knownChange) {
    return point.knownChange.note;
  }
  return point.forecast === 'STEO'
    ? 'the U.S. government’s short-term national forecast'
    : 'the selected forecast view';
};

const reasonCopy = ({ kind, point }: BarSlot, formatPercent: (value: number) => string): string => {
  const signed = signedPercent(point.yoyFraction ?? 0, formatPercent);
  if (kind === 'anchor') {
    return `Your bill today. This year’s ${signed} step: ${forecastSourceCopy(point)}.`;
  }
  return point.yoyFraction === null
    ? `Your usage priced at ${point.year}’s actual state-average rate (EIA).`
    : `Rates actually moved ${signed} in ${point.year} (EIA state average).`;
};

// Dot above each historical bar: presence shows a rate change happened, size
// shows how big — the at-a-glance frequency/magnitude texture of real history
const DOT_MAX_PX = 12;
const changeDotPx = (yoyFraction: number): number => Math.min(4 + Math.round(Math.abs(yoyFraction) * 55), DOT_MAX_PX);
// Dense mobile columns are 5px, so the 4–12px scale compresses to 2–5px there or
// neighbouring dots merge into one stripe
const DENSE_MOBILE_DOT_MAX_PX = 5;
const denseMobileDotPx = (px: number): number => Math.max(2, Math.round((px * DENSE_MOBILE_DOT_MAX_PX) / DOT_MAX_PX));

type SlotProps = {
  children: ReactNode;
  h?: { _: string; md: string };
  w?: typeof BAR_W.dense;
};

// Dense mode pins every slot — labels and markers — to the bar's own width so
// nothing widens a column and breaks the horizontal rhythm; the nowrap text
// and the larger markers overflow the centered slot symmetrically instead
const Slot = ({ children, h, w }: SlotProps) => (
  <x.div display="flex" h={h} justifyContent="center" w={w}>
    {children}
  </x.div>
);

type Visibility = { desktop: boolean; mobile: boolean };

type BarProps = {
  dense: boolean;
  maxUsd: number;
  showCaption: Visibility;
  showLabel: Visibility;
  slot: BarSlot;
};

const display = ({ desktop, mobile }: Visibility) => ({
  _: mobile ? 'block' : 'none',
  md: desktop ? 'block' : 'none',
});

const barHeightPx = (usd: number, maxUsd: number, plotH: number): number =>
  Math.max(Math.round((usd / maxUsd) * plotH), MIN_BAR_H);

const Bar = ({ dense, maxUsd, showCaption, showLabel, slot }: BarProps) => {
  const { formatCurrency, formatPercent } = useCurrency();
  const { kind, point } = slot;

  const caption = kind === 'anchor' ? 'Today' : String(point.year);
  const changeDot = point.yoyFraction !== null ? changeDotPx(point.yoyFraction) : null;
  const dotSize =
    changeDot === null
      ? null
      : dense
        ? { _: `${denseMobileDotPx(changeDot)}px`, md: `${changeDot}px` }
        : `${changeDot}px`;
  const slotW = dense ? BAR_W.dense : undefined;

  return (
    <DS.Tooltip
      anchor={
        <DS.Flex alignItems="center" gap={1} w="fit-content">
          <Slot w={slotW}>
            <DS.Text
              color="uiHelperCopy"
              display={display(showLabel)}
              text={{ _: 'body9', md: 'body12' }}
              whiteSpace="nowrap"
            >
              {formatCurrency(roundFive(point.monthlyBillUsd))}
            </DS.Text>
          </Slot>
          {point.knownChange && (
            <Slot w={slotW}>
              <x.span
                bg="uiBg"
                border="2px solid"
                borderColor={BAR_COLOR}
                borderRadius="full"
                display="inline-block"
                h="10px"
                w="10px"
              />
            </Slot>
          )}
          {dotSize !== null && (
            <Slot w={slotW}>
              <x.span
                bg={(point.yoyFraction ?? 0) >= 0 ? BAR_COLOR : 'uiSuccess'}
                borderRadius="full"
                display="inline-block"
                h={dotSize}
                w={dotSize}
              />
            </Slot>
          )}
          <x.div
            bg={BAR_COLOR}
            borderRadius={4}
            h={{
              _: `${barHeightPx(point.monthlyBillUsd, maxUsd, PLOT_H.mobile)}px`,
              md: `${barHeightPx(point.monthlyBillUsd, maxUsd, PLOT_H.desktop)}px`,
            }}
            w={dense ? BAR_W.dense : BAR_W.normal}
          />
          {/* Fixed-height slot: a display:none caption would collapse this row
              and drop the bar below its labeled neighbors' baseline */}
          <Slot h={{ _: `${CAPTION_H.mobile}px`, md: `${CAPTION_H.desktop}px` }} w={slotW}>
            <DS.Text
              color="uiHelperCopy"
              display={display(showCaption)}
              lineHeight="1.2"
              text={{ _: 'body9', md: 'body12' }}
              whiteSpace="nowrap"
            >
              {caption}
            </DS.Text>
          </Slot>
        </DS.Flex>
      }
    >
      <DS.Flex gap={1} textAlign="left">
        <DS.Text fontWeight="medium" text="body14">
          {kind === 'anchor' ? 'Today' : point.year} — about {formatCurrency(roundFive(point.monthlyBillUsd))}/mo
        </DS.Text>
        <DS.Text text="body14">{reasonCopy(slot, formatPercent)}</DS.Text>
      </DS.Flex>
    </DS.Tooltip>
  );
};

type GapToggleProps = {
  collapseLabel: string;
  expanded: boolean;
  expandLabel: string;
  onToggle: () => void;
};

const GapToggle = ({ collapseLabel, expanded, expandLabel, onToggle }: GapToggleProps) => (
  <x.button
    alignItems="center"
    alignSelf="center"
    aria-expanded={expanded}
    aria-label={expanded ? collapseLabel : expandLabel}
    bg="uiBg"
    border="1px solid"
    borderColor="uiDivider"
    borderRadius="full"
    color="uiHelperCopy"
    cursor="pointer"
    display="flex"
    fontSize="16px"
    h="24px"
    justifyContent="center"
    lineHeight="1"
    minW="24px"
    onClick={onToggle}
    type="button"
    w="24px"
  >
    <span aria-hidden>{expanded ? '−' : '+'}</span>
  </x.button>
);

type FanGeometry = {
  captionH: number;
  fontPx: number;
  plotH: number;
  topPad: number;
  width: number;
  /** Label every Nth round year; null labels only the horizon end (the 100px dense-mobile fan) */
  yearStep: number | null;
};

type FanProps = {
  anchor: BillPoint;
  geometry: FanGeometry;
  maxUsd: number;
  projected: BillPoint[];
  scenarioRange?: ScenarioRange;
};

// The forecast fan: a line for the selected view over a shaded band spanning
// the cheapest→most expensive view per year. Everything draws inside the SVG's
// own bounds, so nothing can bleed into the headline above.
const ForecastFan = ({ anchor, geometry, maxUsd, projected, scenarioRange }: FanProps) => {
  const { formatCurrency } = useCurrency();
  const colors = DS.useChartColors();
  const { captionH, fontPx, plotH, topPad, width, yearStep } = geometry;

  const height = topPad + plotH + captionH;
  const baselineY = topPad + plotH;
  const seriesCount = projected.length;
  const xFor = (index: number) => 4 + (index / seriesCount) * (width - 10);
  const yFor = (usd: number) => baselineY - Math.max((usd / maxUsd) * plotH, MIN_BAR_H);

  const linePoints = [anchor, ...projected]
    .map((point, i) => `${xFor(i).toFixed(1)},${yFor(point.monthlyBillUsd).toFixed(1)}`)
    .join(' ');

  const band =
    scenarioRange &&
    [
      `${xFor(0).toFixed(1)},${yFor(anchor.monthlyBillUsd).toFixed(1)}`,
      ...projected.map(
        ({ year }, i) => `${xFor(i + 1).toFixed(1)},${yFor(scenarioRange.byYear[year]?.highUsd ?? 0).toFixed(1)}`
      ),
      ...[...projected]
        .reverse()
        .map(
          (point, i) =>
            `${xFor(seriesCount - i).toFixed(1)},${yFor(scenarioRange.byYear[point.year]?.lowUsd ?? 0).toFixed(1)}`
        ),
    ].join(' ');

  // $ labels + captions at round years, plus the horizon end the headline
  // names; a round year hugging the end would collide with the end label
  const finalYear = projected[seriesCount - 1].year;
  const marked = projected.filter(
    ({ year }, i) => (yearStep !== null && year % yearStep === 0 && finalYear - year >= 2) || i === seriesCount - 1
  );

  return (
    <x.svg
      aria-label={`Forecast to ${projected[seriesCount - 1].year}: selected view as a line, all views as a shaded range`}
      flex="none"
      height={height}
      role="img"
      width={width}
    >
      {band && <x.polygon fill={BAR_COLOR} fillOpacity={0.14} points={band} />}
      <x.polyline
        fill="none"
        points={linePoints}
        stroke={BAR_COLOR}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
      />
      {projected.map(
        (point, i) =>
          point.knownChange && (
            <x.circle
              cx={xFor(i + 1)}
              cy={yFor(point.monthlyBillUsd)}
              fill="uiBg"
              key={point.year}
              r={4}
              stroke={BAR_COLOR}
              strokeWidth={2}
            />
          )
      )}
      {marked.map((point) => {
        const i = projected.indexOf(point);
        const px = xFor(i + 1);
        const anchorSide = i === seriesCount - 1 ? 'end' : 'middle';
        return (
          <x.g key={point.year}>
            <x.circle cx={px} cy={yFor(point.monthlyBillUsd)} fill={BAR_COLOR} r={2.5} />
            <x.text
              fill={colors.axisLabel}
              fontSize={`${fontPx}px`}
              textAnchor={anchorSide}
              x={px}
              y={yFor(point.monthlyBillUsd) - 8}
            >
              {formatCurrency(roundFive(point.monthlyBillUsd))}
            </x.text>
            <x.text fill={colors.tickLabel} fontSize={`${fontPx}px`} textAnchor={anchorSide} x={px} y={height - 2}>
              {point.year}
            </x.text>
          </x.g>
        );
      })}
    </x.svg>
  );
};

// shortLabel where the full one can't fit 375px — six chips at body12 never
// could, which is what wrapped the legend to three ragged rows
const LegendChip = ({ label, shortLabel, swatch }: { label: string; shortLabel?: string; swatch: ReactNode }) => (
  // w="fit-content" overrides DS.Flex's default full width so the chips share one centered line
  <DS.Flex alignItems="center" flexDirection="row" gap={1} w="fit-content">
    {swatch}
    <DS.Text color="uiHelperCopy" display={shortLabel ? { _: 'none', md: 'block' } : 'block'} text="body12">
      {label}
    </DS.Text>
    {shortLabel ? (
      <DS.Text color="uiHelperCopy" display={{ _: 'block', md: 'none' }} text="body12">
        {shortLabel}
      </DS.Text>
    ) : null}
  </DS.Flex>
);

// Two deliberate rows — the series, then the markers — instead of six chips
// wrapping wherever they land. Desktop fits both groups on one line.
const LegendRow = ({ children }: { children: ReactNode }) => (
  <DS.Flex flexDirection="row" flexWrap="nowrap" gap={4} justifyContent="center" w="fit-content">
    {children}
  </DS.Flex>
);

export const MonthlyBillChart = ({ deepHistory, points, scenarioRange, scenarioSelector }: Props) => {
  const { formatCurrency } = useCurrency();
  const [expandedPast, setExpandedPast] = useState(false);

  const history = points.filter(({ isProjected }) => !isProjected);
  const projected = points.filter(({ isProjected }) => isProjected);
  const displayedHistory = expandedPast && deepHistory ? [...deepHistory, ...history] : history;
  const dense = expandedPast;

  const anchor = history[history.length - 1];
  const final = projected[projected.length - 1];
  const firstYear = displayedHistory[0].year;
  const slots: BarSlot[] = displayedHistory.map((point) => ({
    kind: point.year === anchor.year ? 'anchor' : 'history',
    point,
  }));

  // Which markers the plot actually draws: the sized dots ride the bars, the
  // announced ring appears on a bar or on the fan
  const markers = {
    announced: [...displayedHistory, ...projected].some(({ knownChange }) => knownChange !== undefined),
    fell: displayedHistory.some(({ yoyFraction }) => yoyFraction !== null && yoyFraction < 0),
    rose: displayedHistory.some(({ yoyFraction }) => yoyFraction !== null && yoyFraction >= 0),
  };

  const maxUsd = Math.max(
    ...displayedHistory.map(({ monthlyBillUsd }) => monthlyBillUsd),
    ...projected.map(({ monthlyBillUsd }) => monthlyBillUsd),
    ...projected.map(({ year }) => scenarioRange?.byYear[year]?.highUsd ?? 0)
  );
  const gap = dense ? ROW_GAP.dense : ROW_GAP.normal;

  // Collapsed: mobile keeps labels only where they carry the story; desktop
  // shows all. Dense (2005 expanded): both viewports thin out so 22 columns
  // of labels don't collide.
  const showLabel = ({ kind, point }: BarSlot): Visibility => {
    const story = kind === 'anchor' || point.year === firstYear;
    return { desktop: dense ? story : true, mobile: story };
  };
  const showCaption = ({ kind, point }: BarSlot): Visibility => {
    const story = kind === 'anchor' || point.year === firstYear;
    // A round year hugging Today would collide with the Today caption
    const roundYear = point.year % 5 === 0 && anchor.year - point.year >= 2;
    return {
      desktop: dense ? story || roundYear : true,
      mobile: dense ? story || roundYear : story,
    };
  };

  const fan = (geometry: FanGeometry) => (
    <ForecastFan
      anchor={anchor}
      geometry={geometry}
      maxUsd={maxUsd}
      projected={projected}
      scenarioRange={scenarioRange}
    />
  );

  return (
    <DS.Flex as="figure" gap={3} m={0} maxW="720px" mx="auto" w="100%">
      {/* maxW kills the orphaned last word on desktop */}
      <DS.Text as="figcaption" maxW="560px" mx="auto" text={{ _: 'body16', md: 'body20' }} textAlign="center">
        <span>At your usage, your {formatCurrency(Math.round(anchor.monthlyBillUsd))} bill could be </span>
        <x.b>~{formatCurrency(roundFive(final.monthlyBillUsd))}/mo</x.b>
        <span> by {final.year}.</span>
      </DS.Text>

      <DS.Flex
        alignItems="flex-end"
        aria-label={`Monthly electric bill: actual history ${firstYear}–${anchor.year - 1}, your bill today in ${anchor.year}, then a forecast to ${final.year}`}
        flexDirection="row"
        gap={gap}
        justifyContent="center"
        role="group"
      >
        {deepHistory && (
          <GapToggle
            collapseLabel="Hide the past 20 years"
            expandLabel="Show the past 20 years"
            expanded={expandedPast}
            onToggle={() => setExpandedPast((current) => !current)}
          />
        )}
        {slots.map((slot) => (
          <Bar
            dense={dense}
            key={slot.point.year}
            maxUsd={maxUsd}
            showCaption={showCaption(slot)}
            showLabel={showLabel(slot)}
            slot={slot}
          />
        ))}
        {/* One fan per breakpoint: SVG text can't reflow responsively */}
        <x.div display={{ _: 'block', md: 'none' }}>
          {fan({
            captionH: CAPTION_H.mobile,
            fontPx: 9,
            plotH: PLOT_H.mobile,
            topPad: FAN_TOP_PAD.mobile,
            width: dense ? FAN_W.mobileDense : FAN_W.mobile,
            yearStep: dense ? null : 5,
          })}
        </x.div>
        <x.div display={{ _: 'none', md: 'block' }}>
          {fan({
            captionH: CAPTION_H.desktop,
            fontPx: 12,
            plotH: PLOT_H.desktop,
            topPad: FAN_TOP_PAD.desktop,
            width: dense ? FAN_W.desktopDense : FAN_W.desktop,
            yearStep: 5,
          })}
        </x.div>
      </DS.Flex>

      {scenarioSelector}

      <DS.Flex
        aria-label="Chart legend"
        flexDirection="row"
        flexWrap="wrap"
        gap={4}
        justifyContent="center"
        role="group"
      >
        <LegendRow>
          <LegendChip
            label="Actual (EIA)"
            swatch={<x.span bg={BAR_COLOR} borderRadius={2} display="inline-block" h={3} w={3} />}
          />
          <LegendChip
            label="Forecast (selected view)"
            shortLabel="Forecast"
            swatch={<x.span bg={BAR_COLOR} borderRadius={2} display="inline-block" h="2px" w="14px" />}
          />
          <LegendChip
            label="Range of all views"
            shortLabel="All views"
            swatch={<x.span bg={BAR_COLOR} borderRadius={2} display="inline-block" h="10px" opacity={0.2} w="14px" />}
          />
        </LegendRow>
        {/* Only the markers actually drawn: "Rate fell" and "Announced change"
            explain nothing in a state whose collapsed view has neither */}
        {(markers.announced || markers.fell || markers.rose) && (
          <LegendRow>
            {markers.rose && (
              <LegendChip
                label="Rate rose (size = how much)"
                shortLabel="Rate rose"
                swatch={<x.span bg={BAR_COLOR} borderRadius="full" display="inline-block" h="9px" w="9px" />}
              />
            )}
            {markers.fell && (
              <LegendChip
                label="Rate fell"
                swatch={<x.span bg="uiSuccess" borderRadius="full" display="inline-block" h="9px" w="9px" />}
              />
            )}
            {markers.announced && (
              <LegendChip
                label="Announced change"
                swatch={
                  <x.span
                    bg="uiBg"
                    border="2px solid"
                    borderColor={BAR_COLOR}
                    borderRadius="full"
                    display="inline-block"
                    h="10px"
                    w="10px"
                  />
                }
              />
            )}
          </LegendRow>
        )}
      </DS.Flex>

      {/* Crawlable, screen-reader-friendly full series behind the visualization.
          The visually-hidden recipe lives on a wrapper div — overflow:hidden has
          no effect on a <table> box, so a bare hidden table leaks its full row
          width into the page's horizontal scroll on mobile */}
      <div
        style={{
          border: 0,
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          height: '1px',
          margin: '-1px',
          overflow: 'hidden',
          padding: 0,
          position: 'absolute',
          whiteSpace: 'nowrap',
          width: '1px',
        }}
      >
        <table>
          <caption>Estimated monthly electric bill by year</caption>
          <thead>
            <tr>
              <th scope="col">Year</th>
              <th scope="col">Monthly bill</th>
              <th scope="col">Rate change</th>
              <th scope="col">Basis</th>
            </tr>
          </thead>
          <tbody>
            {[...(deepHistory ?? []), ...points].map(
              ({ forecast, isProjected, knownChange, monthlyBillUsd, year, yoyFraction }) => (
                <HiddenRow
                  forecast={forecast}
                  isAnchor={year === anchor.year}
                  isProjected={isProjected}
                  key={year}
                  knownChangeNote={knownChange?.note}
                  monthlyBillUsd={monthlyBillUsd}
                  year={year}
                  yoyFraction={yoyFraction}
                />
              )
            )}
          </tbody>
        </table>
      </div>
    </DS.Flex>
  );
};

type HiddenRowProps = {
  forecast?: 'STEO' | ProjectionScenario;
  isAnchor: boolean;
  isProjected: boolean;
  knownChangeNote?: string;
  monthlyBillUsd: number;
  year: number;
  yoyFraction: number | null;
};

// Only completed EIA years are "actual": Today is the entered bill plus this
// year's step, whose AEO fallback is picker-independent (see todayStepFraction)
const basisCopy = ({
  forecast,
  isAnchor,
  isProjected,
  knownChangeNote,
}: Pick<HiddenRowProps, 'forecast' | 'isAnchor' | 'isProjected' | 'knownChangeNote'>): string => {
  if (isAnchor) {
    const step = knownChangeNote ?? (forecast === 'STEO' ? 'EIA short-term forecast' : 'EIA long-term outlook');
    return `Your bill today; this year’s step: ${step}`;
  }
  if (knownChangeNote) {
    return knownChangeNote;
  }
  if (isProjected) {
    return forecast === 'STEO' ? 'EIA short-term forecast' : 'Selected forecast view';
  }
  return 'EIA actual';
};

const HiddenRow = ({
  forecast,
  isAnchor,
  isProjected,
  knownChangeNote,
  monthlyBillUsd,
  year,
  yoyFraction,
}: HiddenRowProps) => {
  const { formatCurrency, formatPercent } = useCurrency();
  return (
    <tr>
      <th scope="row">{year}</th>
      <td>{formatCurrency(Math.round(monthlyBillUsd))}</td>
      <td>{yoyFraction === null ? '—' : signedPercent(yoyFraction, formatPercent)}</td>
      <td>{basisCopy({ forecast, isAnchor, isProjected, knownChangeNote })}</td>
    </tr>
  );
};
